// src/hooks/useTimer.ts
import { useState, useEffect, useRef, useCallback } from 'react';

export const useTimer = (initialTime: number, onTimeUp?: () => void) => {
  const [time, setTime] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(false);
  const onTimeUpRef = useRef(onTimeUp);

  // Keep ref pointing to latest callback on every render — avoids stale closures
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  });

  useEffect(() => {
    if (!isRunning || time <= 0) return;
    const t = setTimeout(() => {
      setTime((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearTimeout(t);
  }, [isRunning, time]);

  // Fire callback when countdown reaches zero
  useEffect(() => {
    if (time === 0 && isRunning) {
      setIsRunning(false);
      onTimeUpRef.current?.();
    }
  }, [time, isRunning]);

  const start = useCallback(() => setIsRunning(true), []);
  const pause = useCallback(() => setIsRunning(false), []);
  const reset = useCallback((newTime?: number) => {
    setIsRunning(false);
    setTime(newTime ?? initialTime);
  }, [initialTime]);

  return { time, isRunning, start, pause, reset };
};